import type {
  ChangeRequest,
  ModerationCard,
  ModerationPendingSlot,
  ModerationVersion,
  ModifierGroupCard,
} from '../model/schema';

/** Pending slots in their fixed review order: the merchant's, then the sync's. */
export const PENDING_SLOTS: readonly ModerationPendingSlot[] = ['manual', 'sync'];

/** A pending slot together with the version it holds. */
export interface PendingSlotVersion {
  slot: ModerationPendingSlot;
  version: ModerationVersion;
}

/**
 * The card's pending versions (`manual` then `sync`), present slots only.
 * A slot is pending by construction — the card holds at most one request per
 * source, and only while it waits.
 */
export function pendingSlotsOf(
  card: Pick<ModerationCard, 'manual' | 'sync'> | ModifierGroupCard | null | undefined,
): PendingSlotVersion[] {
  if (!card) return [];
  return PENDING_SLOTS.flatMap((slot) => {
    const version = card[slot];
    return version ? [{ slot, version }] : [];
  });
}

/**
 * The slot that holds this request: matched by request id first (exact), by
 * `source` second (the card stamps one request per source). `undefined`
 * when the card no longer carries it — decided since, or superseded.
 */
export function slotOfRequest(
  card: ModerationCard | null | undefined,
  request: Pick<ChangeRequest, 'id' | 'source'>,
): PendingSlotVersion | undefined {
  const slots = pendingSlotsOf(card);
  const byId = slots.find(({ version }) => version.change_request?.id === request.id);
  if (byId) return byId;
  const source = (request.source ?? '').trim().toLowerCase();
  return slots.find(
    ({ slot, version }) =>
      slot === source && (version.change_request?.id ?? request.id) === request.id,
  );
}

/** Every present version of the card (`prod` first) for slot-agnostic grids. */
export function cardVersions(
  card: ModerationCard | null | undefined,
): ModerationVersion[] {
  if (!card) return [];
  return [card.prod, card.manual, card.sync].filter(
    (version): version is ModerationVersion => version !== null && version !== undefined,
  );
}
